import { memo, type ReactNode } from 'react'
import { Button, Text, VStack } from '@chakra-ui/react'
import type { WalkStop } from '../../../lib/walkStops'
import type { ViewMode } from '../../../lib/walkPrefs'
import { WalkStopRow, residentsSummary } from './WalkStopRow'

interface Props {
  stops: WalkStop[]; sequence: ReadonlyMap<string, number>; viewMode: ViewMode
  selectedKey: string | null
  /** Contacts view: the person the dock names — only that person's row is pressed. */
  selectedContactId?: string | null
  /** Contacts-view rows pass the tapped person's contactId too. Keep it stable (I-7). */
  onSelect: (key: string, contactId?: string) => void; showKindIcons: boolean
  emptyMessage: string; onShowAll?: () => void
}

const houseNumber = (a: string) => parseInt(a, 10) || 0

/**
 * [Spec §4.3] The walk list. `households`: open stops in walk order with street headers, then
 * a Done section. `byStreet`: streets A→Z, house number within. `contacts`: one row per person.
 */
export const WalkStopList = memo(function WalkStopList({
  stops, sequence, viewMode, selectedKey, selectedContactId = null, onSelect, showKindIcons, emptyMessage, onShowAll,
}: Props) {
  if (stops.length === 0) {
    return (
      <VStack py={10} gap={2}>
        <Text color="fg.muted">{emptyMessage}</Text>
        {onShowAll && <Button variant="ghost" minH="44px" onClick={onShowAll}>Show all</Button>}
      </VStack>
    )
  }

  const row = (s: WalkStop, primary?: string, secondary?: string, contactId?: string) => (
    <WalkStopRow key={contactId ? `${s.key}|${contactId}` : s.key} stop={s} contactId={contactId}
      seq={sequence.get(s.key)}
      selected={s.key === selectedKey && (contactId == null || selectedContactId == null || contactId === selectedContactId)}
      showKindIcon={showKindIcons} onSelect={onSelect}
      primary={primary ?? (s.kind === 'worksite' && s.workplaceLabel ? s.workplaceLabel : s.address)}
      secondary={secondary ?? (s.kind === 'worksite' ? s.address : residentsSummary(s))} />
  )
  const header = (text: string, key: string) => (
    <Text key={key} fontSize="xs" fontWeight="bold" color="fg.muted" textTransform="uppercase" letterSpacing="wider" pt={2}>
      {text}
    </Text>
  )

  if (viewMode === 'contacts') {
    return (
      <VStack align="stretch" gap={2}>
        {stops.flatMap(s => s.residents.map(r =>
          row(s, `${r.firstName} ${r.lastName}`.trim(), s.address, r.contactId)))}
      </VStack>
    )
  }

  if (viewMode === 'byStreet') {
    const byStreet = new Map<string, WalkStop[]>()
    for (const s of stops) byStreet.set(s.street, [...(byStreet.get(s.street) ?? []), s])
    return (
      <VStack align="stretch" gap={2}>
        {[...byStreet.entries()].sort(([a], [b]) => a.localeCompare(b)).flatMap(([street, list]) => [
          header(street, `h-${street}`),
          ...[...list].sort((a, b) => houseNumber(a.address) - houseNumber(b.address)).map(s => row(s)),
        ])}
      </VStack>
    )
  }

  const open = stops.filter(s => s.state !== 'done')
  const done = stops.filter(s => s.state === 'done')
  const items: ReactNode[] = []
  let street: string | null = null
  open.forEach((s, i) => {
    // Keyed by position: a street can recur after a change (Elm -> Oak -> Elm), so its name isn't unique.
    if (s.street !== street) {
      items.push(header(street == null ? `Up next on ${s.street}` : s.street, `h-${i}`))
      street = s.street
    }
    items.push(row(s))
  })
  if (done.length > 0) {
    items.push(header(`Done (${done.length})`, 'h-done'))
    done.forEach(s => items.push(row(s)))
  }
  return <VStack align="stretch" gap={2}>{items}</VStack>
})
